import { motion } from 'framer-motion';
import { FACCIONES, type FactionId } from '@/types';
import { useWorldStore } from '@/engine/state';
import { FullscreenPage } from '@/components/ui';

const FACTION_IDS: FactionId[] = [
  'clan_rio',
  'guardianes_fuego',
  'susurradores',
  'sombra',
];

/**
 * Flag que marca una facción como conocida. Se setea en las escenas
 * donde el jugador la encuentra por primera vez.
 */
function flagConocida(id: FactionId): string {
  return `conocido_${id}`;
}

function descriptor(valor: number): string {
  if (valor >= 60) return 'Hermandad probada';
  if (valor >= 25) return 'Aliado';
  if (valor >= 5) return 'Bienvenido';
  if (valor > -5) return 'Neutral';
  if (valor > -25) return 'Sospechoso';
  if (valor > -60) return 'Enemigo';
  return 'Odio eterno';
}

export function FactionsPanel() {
  const reputacion = useWorldStore((s) => s.reputacion);
  const flags = useWorldStore((s) => s.flags);

  const conocidas = FACTION_IDS.filter((id) => flags[flagConocida(id)]);
  const totalPorDescubrir = FACTION_IDS.length - conocidas.length;

  return (
    <FullscreenPage
      titulo="Facciones"
      subtitulo={
        conocidas.length === 0
          ? 'Todavía no conocés a ninguna. Avanzá en la historia.'
          : `Conocés ${conocidas.length} de ${FACTION_IDS.length}.`
      }
    >
      <ul className="space-y-3 max-w-3xl mx-auto">
        {conocidas.map((id) => {
          const f = FACCIONES[id];
          const valor = reputacion[id];
          const pct = (valor + 100) / 2;
          return (
            <motion.li
              key={id}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              className="rounded-lg border p-3 md:p-4 bg-ash-950/60"
              style={{ borderColor: `${f.color}66` }}
            >
              <header className="flex items-baseline justify-between gap-2">
                <h2 className="text-display fluid-base text-ash-100 truncate">
                  {f.nombre}
                </h2>
                <span
                  className="fluid-xs font-ui uppercase tracking-widest shrink-0"
                  style={{ color: f.color }}
                >
                  {descriptor(valor)}
                </span>
              </header>
              <p className="fluid-xs text-ash-400 mt-1 text-narrative italic clamp-2">
                Líder: {f.lider}. {f.descripcion}
              </p>
              <div className="mt-3 h-1.5 bg-ash-900 rounded overflow-hidden relative">
                <div
                  className="h-full transition-[width] duration-700"
                  style={{ width: `${pct}%`, backgroundColor: f.color }}
                />
                <div className="absolute top-0 bottom-0 left-1/2 w-px bg-ash-700" aria-hidden />
              </div>
              <p className="fluid-xs text-ash-500 font-ui mt-1 tabular-nums">
                Reputación: {valor > 0 ? '+' : ''}
                {valor} / ±100
              </p>
            </motion.li>
          );
        })}

        {totalPorDescubrir > 0 && (
          <motion.li
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="rounded-lg border border-dashed border-ash-800 p-3 md:p-4 bg-ash-950/30 text-center"
          >
            <p className="fluid-xs font-ui uppercase tracking-widest text-ash-600">
              {totalPorDescubrir} facción{totalPorDescubrir !== 1 ? 'es' : ''} por descubrir
            </p>
            <p className="fluid-xs italic text-ash-700 mt-1">
              El bosque todavía tiene voces que no escuchaste.
            </p>
          </motion.li>
        )}
      </ul>
    </FullscreenPage>
  );
}
